import type { SetPrescription, MeasureType, WeightUnit } from './types';

/* ---------- set text ⇄ SetPrescription ----------
   Coaches type sets the way they write them in the sheet:
   "55x10" · "30s" · "160 x 3 laps" · "Blue band x 15" · "AMRAP" · "BW".
   Shared by the SessionEditor, the live logger, the exercise picker, the
   per-client Training Log, and the workout-history seed script. */

export const setToText = (s: SetPrescription): string => {
  if (s.load && s.reps) return `${s.load}x${s.reps}`;
  if (s.load && s.seconds) return `${s.load} x ${s.seconds}s`;
  if (s.load && s.laps) return `${s.load} x ${s.laps} laps`;
  if (s.seconds) return `${s.seconds}s`;
  if (s.laps) return `${s.laps} laps`;
  if (s.reps) return s.reps;
  // Load-only set: a bare number would re-parse as reps, so tag a unitless
  // numeric load with "lb" to keep the round-trip lossless.
  if (s.load) return /^\d+(\.\d+)?$/.test(s.load) ? `${s.load} lb` : s.load;
  return '';
};

const parseMeasure = (text: string): SetPrescription | null => {
  const secs = text.match(/^(\d+)\s*s(ec(onds)?)?$/i);
  if (secs) return { seconds: parseInt(secs[1], 10) };
  const mins = text.match(/^(\d+(?:\.\d+)?)\s*min(ute)?s?$/i);
  if (mins) return { seconds: Math.round(parseFloat(mins[1]) * 60) };
  const laps = text.match(/^(\d+)\s*laps?$/i);
  if (laps) return { laps: parseInt(laps[1], 10) };
  return null;
};

// A lone token with no "load × measure" structure: decide reps vs load.
// Reps cover bare numbers, ranges (8-10), per-side (10/leg), percentages (75%), AMRAP.
// Everything else (BW, band colours, "135 lb", "26kg") is a load.
const looksLikeReps = (text: string): boolean =>
  /amrap/i.test(text) ||
  /^\d+(\.\d+)?$/.test(text) ||
  /^\d+\s*[-–]\s*\d+$/.test(text) ||
  /^\d+\s*\/\s*\w+$/.test(text) ||
  /^\d+(\.\d+)?\s*%$/.test(text);

export const parseSet = (raw: string): SetPrescription => {
  const text = raw.trim();
  if (!text) return {};
  const parts = text.split(/\s*[×x·]\s*/i).filter(Boolean);
  if (parts.length >= 2) {
    const load = parts[0];
    const measureText = parts.slice(1).join(' ');
    const measure = parseMeasure(measureText);
    return measure ? { load, ...measure } : { load, reps: measureText };
  }
  const measure = parseMeasure(text);
  if (measure) return measure;
  if (looksLikeReps(text)) return { reps: text };
  return { load: text }; // "BW", "Blue band", "135 lb", "26kg"
};

/* ---------- measure & unit presentation ----------
   Coaches pick a measure TYPE per exercise (Weight × Reps / Time / Laps /
   Bodyweight / Other) and a weight unit; everything stored before that
   choice existed is inferred from the set data itself. */

export const MEASURE_LABELS: Record<MeasureType, string> = {
  weight_reps: 'Weight × Reps',
  time: 'Time',
  laps: 'Laps',
  bodyweight: 'Bodyweight',
  other: 'Other',
};

const NUMERIC_LOAD = /^\d+(\.\d+)?$/;

// "26kg" / "135 lb" / "135lbs" → split the unit off the load.
export const normalizeLoad = (load: string): { load: string; unit?: WeightUnit } => {
  const m = load.trim().match(/^(\d+(?:\.\d+)?)\s*(kg|kgs|lb|lbs)\.?$/i);
  if (!m) return { load: load.trim() };
  return { load: m[1], unit: /^kg/i.test(m[2]) ? 'kg' : 'lb' };
};

// What kind of exercise do these sets describe? (Used when ex.measure is unset.)
export const inferMeasure = (sets: SetPrescription[]): MeasureType => {
  const filled = sets.filter((s) => Object.keys(s).length > 0);
  if (filled.length === 0) return 'weight_reps';
  if (filled.some((s) => s.laps != null)) return 'laps';
  if (filled.every((s) => s.seconds != null && s.load == null)) return 'time';
  if (filled.some((s) => s.seconds != null)) return 'other'; // mixed time + load
  if (filled.some((s) => s.load != null && !NUMERIC_LOAD.test(s.load))) return 'other'; // "Blue band", "BW"
  if (filled.every((s) => s.load == null && s.reps != null)) return 'bodyweight';
  return 'weight_reps';
};

// One set, formatted for reading: "225 lb × 10" · "60 sec" · "160 lb × 3 laps" · "12 reps".
export const fmtSet = (s: SetPrescription, measure: MeasureType, unit: WeightUnit): string => {
  if (Object.keys(s).length === 0) return '';
  switch (measure) {
    case 'weight_reps':
      if (s.load && s.reps) return `${s.load} ${unit} × ${s.reps}`;
      if (s.load) return `${s.load} ${unit}`;
      return s.reps ? `${s.reps}` : setToText(s);
    case 'time':
      return s.seconds != null ? `${s.seconds} sec` : setToText(s);
    case 'laps':
      if (s.laps != null) return s.load ? `${s.load} ${unit} × ${s.laps} laps` : `${s.laps} laps`;
      return setToText(s);
    case 'bodyweight':
      return s.reps ? `${s.reps} reps` : setToText(s);
    default:
      return setToText(s);
  }
};
